import { useMemo, useRef, useState, useEffect } from "react";
import { Lock, Unlock, Check, Circle } from "lucide-react";
import { initials, colorFor } from "@/lib/avatar";
import { dueDateStatus } from "@/components/plan/dueDateStatus";

// ── Layout constants ─────────────────────────────────────────────────────────
const DAY_WIDTH = 28;
const ROW_HEIGHT = 36;
const ROW_GAP = 4;
const SWIMLANE_HEADER = 28;
const SWIMLANE_PADDING_TOP = 8;
const SWIMLANE_PADDING_BOTTOM = 12;
const HEADER_HEIGHT = 52;
const LEFT_GUTTER = 240;
const HANDLE_RADIUS = 4;

const PHASE_COLORS = {
  pre:  { bar: "#14b8a6", bg: "rgba(20,184,166,0.06)",  label: "Pre-Production",  text: "#5eead4" },
  prod: { bar: "#f59e0b", bg: "rgba(245,158,11,0.06)",  label: "Production",       text: "#fcd34d" },
  post: { bar: "#a78bfa", bg: "rgba(167,139,250,0.06)", label: "Post-Production", text: "#c4b5fd" },
};
const PHASE_ORDER = ["pre", "prod", "post"];

// ── Date helpers ─────────────────────────────────────────────────────────────
const parseDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
};
const addDays = (date, n) => {
  const d = new Date(date.getTime());
  d.setUTCDate(d.getUTCDate() + n);
  return d;
};
const daysBetween = (a, b) => Math.round((b - a) / (24 * 60 * 60 * 1000));
const fmtMonth = (d) => d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const isoOf = (d) => d.toISOString().slice(0, 10);

// ── Main ─────────────────────────────────────────────────────────────────────
export default function GanttChart({
  plan,
  tasks,
  dependencies = [],
  conflictingTaskIds = new Set(),
  members = [],
  allocations = {},
  onTaskClick,
  onToggleLock,
  onToggleComplete,
  onMoveTask,
  onCreateDependency,
  onDeleteDependency,
  readonly = false,
}) {
  const scrollRef = useRef(null);
  const svgRef = useRef(null);

  // Linking state for drag-to-create-dependency
  const [linking, setLinking] = useState(null); // { fromTaskId, cursorX, cursorY } | null
  const [hoverTargetId, setHoverTargetId] = useState(null);

  // Bucket tasks by phase
  const grouped = useMemo(() => {
    const out = { pre: [], prod: [], post: [] };
    (tasks || []).forEach((t) => { if (out[t.phase]) out[t.phase].push(t); });
    Object.keys(out).forEach((p) => {
      out[p].sort((a, b) => {
        const ad = a.start || "";
        const bd = b.start || "";
        if (ad !== bd) return ad.localeCompare(bd);
        return (a.created_at || "").localeCompare(b.created_at || "");
      });
    });
    return out;
  }, [tasks]);

  const memberById = useMemo(
    () => Object.fromEntries((members || []).map((m) => [m.user_id, m])),
    [members],
  );

  const hoursPerDay = plan?.working_hours_per_day || 8;

  // Compute peak-load ratio for a task across its working-day span.
  // Returns 0 if unassigned. Used by the load-ring decoration around avatars.
  const getLoadRatio = (task) => {
    if (!task.assigned_user_id) return 0;
    const userLoad = allocations[task.assigned_user_id] || {};
    const start = parseDate(task.start);
    const end = parseDate(task.end || task.start);
    if (!start || !end) return 0;
    let maxRatio = 0;
    let cur = start;
    let safety = 0;
    while (cur <= end && safety < 365) {
      const iso = cur.toISOString().slice(0, 10);
      const hrs = userLoad[iso] || 0;
      const ratio = hrs / hoursPerDay;
      if (ratio > maxRatio) maxRatio = ratio;
      cur = addDays(cur, 1);
      safety += 1;
    }
    return maxRatio;
  };

  const loadRingColor = (ratio) => {
    if (ratio > 1.0) return "#f43f5e";   // rose — over capacity
    if (ratio >= 0.8) return "#f59e0b";  // amber — 80%+
    if (ratio > 0) return "#10b981";     // emerald — healthy
    return null;                          // no load → no ring
  };

  // Timeline window
  const { startDate, totalDays } = useMemo(() => {
    const dates = [];
    (tasks || []).forEach((t) => {
      const s = parseDate(t.start);
      const e = parseDate(t.end || t.start);
      if (s) dates.push(s);
      if (e) dates.push(e);
    });
    let start;
    let end;
    if (dates.length === 0) {
      start = new Date();
      start.setUTCHours(0, 0, 0, 0);
      end = addDays(start, 30);
    } else {
      start = new Date(Math.min(...dates.map((d) => d.getTime())));
      end = new Date(Math.max(...dates.map((d) => d.getTime())));
    }
    start = addDays(start, -5);
    end = addDays(end, 10);
    return { startDate: start, endDate: end, totalDays: daysBetween(start, end) + 1 };
  }, [tasks]);

  // Working day calendar
  const workingDays = plan?.working_days || [1, 2, 3, 4, 5];
  const holidaysSet = useMemo(() => new Set(plan?.holidays || []), [plan?.holidays]);
  const isOffDay = (d) => {
    const dow = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    if (!workingDays.includes(dow)) return true;
    if (holidaysSet.has(isoOf(d))) return true;
    return false;
  };

  // Auto-scroll to today
  useEffect(() => {
    if (!scrollRef.current) return;
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const offset = daysBetween(startDate, today);
    if (offset > 0 && offset < totalDays) {
      scrollRef.current.scrollLeft = Math.max(0, offset * DAY_WIDTH - 200);
    }
  }, [startDate, totalDays]);

  // ── Compute per-task render position ──────────────────────────────────────
  // We need this for arrow rendering AND for hit-testing during link drags.
  const { swimlaneHeights, taskPositions } = useMemo(() => {
    const heights = PHASE_ORDER.map((p) => {
      const n = grouped[p].length;
      const effective = Math.max(n, 1);
      return SWIMLANE_HEADER + SWIMLANE_PADDING_TOP + effective * (ROW_HEIGHT + ROW_GAP) + SWIMLANE_PADDING_BOTTOM;
    });
    const positions = {};
    let yCursor = HEADER_HEIGHT;
    PHASE_ORDER.forEach((phase, laneIdx) => {
      const laneY = yCursor;
      yCursor += heights[laneIdx];
      grouped[phase].forEach((task, idx) => {
        const rowY = laneY + SWIMLANE_HEADER + SWIMLANE_PADDING_TOP + idx * (ROW_HEIGHT + ROW_GAP);
        const taskStart = parseDate(task.start);
        const taskEnd = parseDate(task.end || task.start);
        if (!taskStart) return;
        const startCol = daysBetween(startDate, taskStart);
        const endCol = taskEnd ? daysBetween(startDate, taskEnd) : startCol;
        const subDay = task.duration < 1;
        const widthDays = subDay ? task.duration : Math.max(1, endCol - startCol + 1);
        const x = LEFT_GUTTER + startCol * DAY_WIDTH;
        const w = widthDays * DAY_WIDTH - 2;
        positions[task.id] = {
          x,
          y: rowY,
          width: w,
          rightX: x + w,
          centerY: rowY + ROW_HEIGHT / 2,
          rowY,
        };
      });
    });
    return { swimlaneHeights: heights, taskPositions: positions };
  }, [grouped, startDate]);

  const totalHeight = HEADER_HEIGHT + swimlaneHeights.reduce((a, b) => a + b, 0);
  const totalWidth = LEFT_GUTTER + totalDays * DAY_WIDTH;

  // Month bands
  const monthBands = useMemo(() => {
    const bands = [];
    let cursor = new Date(startDate.getTime());
    let bandStart = 0;
    let currentLabel = fmtMonth(cursor);
    for (let i = 0; i < totalDays; i++) {
      const lbl = fmtMonth(cursor);
      if (lbl !== currentLabel) {
        bands.push({ label: currentLabel, start: bandStart, width: i - bandStart });
        bandStart = i;
        currentLabel = lbl;
      }
      cursor = addDays(cursor, 1);
    }
    bands.push({ label: currentLabel, start: bandStart, width: totalDays - bandStart });
    return bands;
  }, [startDate, totalDays]);

  // ── Linking ────────────────────────────────────────────────────────────────
  const startLink = (taskId, e) => {
    if (readonly || !onCreateDependency) return;
    e.stopPropagation();
    e.preventDefault();
    const svgRect = svgRef.current.getBoundingClientRect();
    setLinking({
      fromTaskId: taskId,
      cursorX: e.clientX - svgRect.left + (scrollRef.current?.scrollLeft || 0),
      cursorY: e.clientY - svgRect.top + (scrollRef.current?.scrollTop || 0),
    });

    const handleMove = (ev) => {
      const r = svgRef.current.getBoundingClientRect();
      setLinking((cur) => cur && {
        ...cur,
        cursorX: ev.clientX - r.left + (scrollRef.current?.scrollLeft || 0),
        cursorY: ev.clientY - r.top + (scrollRef.current?.scrollTop || 0),
      });

      // hit-test: find a task at this cursor
      const cx = ev.clientX - r.left + (scrollRef.current?.scrollLeft || 0);
      const cy = ev.clientY - r.top + (scrollRef.current?.scrollTop || 0);
      let hit = null;
      Object.entries(taskPositions).forEach(([tid, p]) => {
        if (tid === taskId) return;
        if (cx >= p.x && cx <= p.x + p.width && cy >= p.rowY && cy <= p.rowY + ROW_HEIGHT) {
          hit = tid;
        }
      });
      setHoverTargetId(hit);
    };
    const handleUp = (ev) => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      const r = svgRef.current.getBoundingClientRect();
      const cx = ev.clientX - r.left + (scrollRef.current?.scrollLeft || 0);
      const cy = ev.clientY - r.top + (scrollRef.current?.scrollTop || 0);
      let hit = null;
      Object.entries(taskPositions).forEach(([tid, p]) => {
        if (tid === taskId) return;
        if (cx >= p.x && cx <= p.x + p.width && cy >= p.rowY && cy <= p.rowY + ROW_HEIGHT) {
          hit = tid;
        }
      });
      if (hit && onCreateDependency) onCreateDependency(taskId, hit);
      setLinking(null);
      setHoverTargetId(null);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  };

  // ── Arrow path builder (cubic-bezier, dep-type aware) ────────────────────
  // Each dep type has different anchor points on the predecessor/dependent:
  //   FS: pred.RIGHT → dep.LEFT
  //   SS: pred.LEFT  → dep.LEFT
  //   FF: pred.RIGHT → dep.RIGHT
  //   SF: pred.LEFT  → dep.RIGHT
  // Control points flare in the direction the arrow "leaves" the bar so the
  // bezier reads naturally regardless of bar order.
  const depEndpoints = (dep, a, b) => {
    const type = dep.type || "FS";
    const fromSide = (type === "FS" || type === "FF") ? "R" : "L";
    const toSide   = (type === "FF" || type === "SF") ? "R" : "L";
    return {
      fromX: fromSide === "R" ? a.rightX : a.x,
      fromY: a.centerY,
      toX:   toSide === "R" ? b.rightX : b.x,
      toY:   b.centerY,
      fromSide,
      toSide,
    };
  };

  const buildArrowPath = (dep, a, b) => {
    const { fromX, fromY, toX, toY, fromSide, toSide } = depEndpoints(dep, a, b);
    // Flare control points OUTWARD from the side they exit/enter.
    const flare = 30;
    const c1x = fromX + (fromSide === "R" ? flare : -flare);
    const c2x = toX   + (toSide   === "R" ? flare : -flare);
    return `M ${fromX} ${fromY} C ${c1x} ${fromY}, ${c2x} ${toY}, ${toX} ${toY}`;
  };

  return (
    <div className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-950">
      <div ref={scrollRef} className="overflow-auto" data-testid="gantt-scroll-container" style={{ maxHeight: "70vh" }}>
        <svg
          ref={svgRef}
          width={totalWidth}
          height={totalHeight}
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: "block", fontFamily: "inherit", userSelect: "none" }}
          data-testid="gantt-svg"
        >
          <defs>
            <marker id="dep-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M 0 0 L 6 4 L 0 8 z" fill="#71717a" />
            </marker>
            <marker id="dep-arrow-conflict" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M 0 0 L 6 4 L 0 8 z" fill="#f43f5e" />
            </marker>
            <marker id="dep-arrow-ghost" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M 0 0 L 6 4 L 0 8 z" fill="#f59e0b" />
            </marker>
          </defs>

          {/* Day-column grid + off-day shading */}
          <g>
            {Array.from({ length: totalDays }).map((_, i) => {
              const d = addDays(startDate, i);
              const x = LEFT_GUTTER + i * DAY_WIDTH;
              const off = isOffDay(d);
              const todayObj = new Date();
              const today =
                d.getUTCFullYear() === todayObj.getUTCFullYear() &&
                d.getUTCMonth() === todayObj.getUTCMonth() &&
                d.getUTCDate() === todayObj.getUTCDate();
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={HEADER_HEIGHT}
                    width={DAY_WIDTH}
                    height={totalHeight - HEADER_HEIGHT}
                    fill={off ? "#0a0a0a" : "transparent"}
                    stroke="#27272a"
                    strokeWidth={i === 0 ? 0 : 0.5}
                  />
                  {today && (
                    <line x1={x + DAY_WIDTH / 2} y1={HEADER_HEIGHT} x2={x + DAY_WIDTH / 2} y2={totalHeight}
                          stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="3,3" opacity={0.7} />
                  )}
                </g>
              );
            })}
          </g>

          {/* Header */}
          <g>
            <rect x={0} y={0} width={totalWidth} height={HEADER_HEIGHT} fill="#09090b" />
            <rect x={0} y={0} width={LEFT_GUTTER} height={HEADER_HEIGHT} fill="#0a0a0a" />
            <line x1={0} y1={HEADER_HEIGHT} x2={totalWidth} y2={HEADER_HEIGHT} stroke="#3f3f46" />
            <line x1={LEFT_GUTTER} y1={0} x2={LEFT_GUTTER} y2={totalHeight} stroke="#3f3f46" />
            {monthBands.map((b, i) => (
              <g key={i}>
                <rect x={LEFT_GUTTER + b.start * DAY_WIDTH} y={0} width={b.width * DAY_WIDTH} height={26}
                      fill={i % 2 === 0 ? "#0a0a0a" : "#0e0e10"} />
                <text x={LEFT_GUTTER + b.start * DAY_WIDTH + 8} y={17}
                      fill="#a1a1aa" fontSize={11} fontWeight={500} letterSpacing={0.5}>
                  {b.label.toUpperCase()}
                </text>
              </g>
            ))}
            {Array.from({ length: totalDays }).map((_, i) => {
              const d = addDays(startDate, i);
              const x = LEFT_GUTTER + i * DAY_WIDTH;
              return (
                <text key={i} x={x + DAY_WIDTH / 2} y={42}
                      fill={isOffDay(d) ? "#52525b" : "#a1a1aa"} fontSize={10} textAnchor="middle">
                  {d.getUTCDate()}
                </text>
              );
            })}
          </g>

          {/* Swimlanes + task rows */}
          {(() => {
            let yCursor = HEADER_HEIGHT;
            return PHASE_ORDER.map((phase, laneIdx) => {
              const phaseTasks = grouped[phase];
              const colors = PHASE_COLORS[phase];
              const laneHeight = swimlaneHeights[laneIdx];
              const laneY = yCursor;
              yCursor += laneHeight;
              return (
                <g key={phase}>
                  <rect x={0} y={laneY} width={totalWidth} height={laneHeight} fill={colors.bg} pointerEvents="none" />
                  <rect x={0} y={laneY} width={LEFT_GUTTER} height={SWIMLANE_HEADER} fill="#09090b" pointerEvents="none" />
                  <line x1={0} y1={laneY} x2={totalWidth} y2={laneY} stroke="#27272a" />
                  <text x={16} y={laneY + 18} fill={colors.text} fontSize={10} fontWeight={700} letterSpacing={1.2}>
                    {colors.label.toUpperCase()}
                  </text>
                  <text x={LEFT_GUTTER - 14} y={laneY + 18} fill="#52525b" fontSize={10} textAnchor="end">
                    {phaseTasks.length} {phaseTasks.length === 1 ? "task" : "tasks"}
                  </text>
                  {phaseTasks.map((task) => {
                    const pos = taskPositions[task.id];
                    if (!pos) return null;
                    return (
                      <TaskRow
                        key={task.id}
                        task={task}
                        pos={pos}
                        phaseColors={colors}
                        assignee={task.assigned_user_id ? memberById[task.assigned_user_id] : null}
                        loadRing={task.assigned_user_id ? loadRingColor(getLoadRatio(task)) : null}
                        onTaskClick={onTaskClick}
                        onToggleLock={onToggleLock}
                        onToggleComplete={onToggleComplete}
                        onMoveTask={onMoveTask}
                        onStartLink={startLink}
                        readonly={readonly}
                        isLinkSource={linking?.fromTaskId === task.id}
                        isHoverTarget={hoverTargetId === task.id}
                        hasConflict={conflictingTaskIds.has(task.id)}
                      />
                    );
                  })}
                  {phaseTasks.length === 0 && (
                    <text x={LEFT_GUTTER + 20} y={laneY + SWIMLANE_HEADER + 30}
                          fill="#3f3f46" fontSize={11} fontStyle="italic">
                      {phase === "prod"
                        ? "No shoot days yet — add a call sheet day to populate this lane."
                        : "No tasks yet — click + Add Task above."}
                    </text>
                  )}
                </g>
              );
            });
          })()}

          {/* Dependency arrows (rendered AFTER bars so they sit on top) */}
          <g pointerEvents="visiblePainted">
            {dependencies.map((dep) => {
              const a = taskPositions[dep.from_id];
              const b = taskPositions[dep.to_id];
              if (!a || !b) return null;
              const isConflict = conflictingTaskIds.has(dep.to_id);
              const path = buildArrowPath(dep, a, b);
              const { fromX, fromY, toX, toY } = depEndpoints(dep, a, b);
              // Midpoint along the curve for the type-badge anchor
              const mx = (fromX + toX) / 2;
              const my = (fromY + toY) / 2 - 8;
              return (
                <g key={dep.id} data-testid={`gantt-dep-${dep.id}`}>
                  <path
                    d={path}
                    fill="none"
                    stroke={isConflict ? "#f43f5e" : "#71717a"}
                    strokeWidth={isConflict ? 2 : 1.5}
                    markerEnd={isConflict ? "url(#dep-arrow-conflict)" : "url(#dep-arrow)"}
                    opacity={0.85}
                  />
                  {/* Type badge — only for non-FS so the chart isn't noisy */}
                  {(dep.type || "FS") !== "FS" && (
                    <g pointerEvents="none">
                      <rect
                        x={mx - 12}
                        y={my - 8}
                        width={24}
                        height={14}
                        rx={2}
                        fill="#0a0a0a"
                        stroke={isConflict ? "#f43f5e" : "#52525b"}
                        strokeWidth={0.75}
                      />
                      <text
                        x={mx}
                        y={my + 2}
                        textAnchor="middle"
                        fontSize={9}
                        fontWeight={700}
                        fill={isConflict ? "#fda4af" : "#a1a1aa"}
                        letterSpacing={0.5}
                      >
                        {dep.type}
                      </text>
                    </g>
                  )}
                  {/* Invisible thicker path for easy clicking */}
                  {!readonly && onDeleteDependency && (
                    <path
                      d={path}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={12}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete this ${dep.type || "FS"} dependency?`)) onDeleteDependency(dep);
                      }}
                    />
                  )}
                </g>
              );
            })}

            {/* Ghost link line during drag */}
            {linking && taskPositions[linking.fromTaskId] && (
              <path
                d={`M ${taskPositions[linking.fromTaskId].rightX} ${taskPositions[linking.fromTaskId].centerY} L ${linking.cursorX} ${linking.cursorY}`}
                fill="none"
                stroke="#f59e0b"
                strokeWidth={2}
                strokeDasharray="4,4"
                markerEnd="url(#dep-arrow-ghost)"
                opacity={0.9}
                pointerEvents="none"
              />
            )}
          </g>
        </svg>
      </div>
    </div>
  );
}

// ── Task row ────────────────────────────────────────────────────────────────
function TaskRow({
  task,
  pos,
  phaseColors,
  assignee,
  loadRing,
  onTaskClick,
  onToggleLock,
  onToggleComplete,
  onMoveTask,
  onStartLink,
  readonly,
  isLinkSource,
  isHoverTarget,
  hasConflict,
}) {
  const [dragging, setDragging] = useState(false);
  const [dragOffsetDays, setDragOffsetDays] = useState(0);
  const [hovered, setHovered] = useState(false);
  const dragRef = useRef({ startX: 0 });

  const isLocked = task.schedule_mode === "locked";
  const isDone = task.kanban_status === "done";
  const barY = pos.y + 4;
  const barH = ROW_HEIGHT - 8;
  const x = pos.x + (dragging ? dragOffsetDays * DAY_WIDTH : 0);
  const barWidth = pos.width;

  const handleBarPointerDown = (e) => {
    if (readonly || !onMoveTask) return;
    e.stopPropagation();
    setDragging(true);
    dragRef.current = { startX: e.clientX };
    const handleMove = (ev) => {
      setDragOffsetDays(Math.round((ev.clientX - dragRef.current.startX) / DAY_WIDTH));
    };
    const handleUp = (ev) => {
      const dDays = Math.round((ev.clientX - dragRef.current.startX) / DAY_WIDTH);
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      setDragging(false);
      setDragOffsetDays(0);
      if (dDays !== 0 && onMoveTask) {
        const s = parseDate(task.start);
        const ns = addDays(s, dDays);
        onMoveTask(task, isoOf(ns));
      }
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  };

  const taskNameY = pos.rowY + ROW_HEIGHT / 2 + 4;
  const stroke = hasConflict ? "#f43f5e" : (isLocked ? "#fbbf24" : (isHoverTarget ? "#f59e0b" : "transparent"));
  const strokeWidth = hasConflict ? 2 : (isLocked || isHoverTarget ? 1.5 : 0);

  return (
    <g
      data-testid={`gantt-task-${task.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <rect x={0} y={pos.rowY} width={LEFT_GUTTER} height={ROW_HEIGHT} fill="#09090b" opacity={0.5} />
      <line x1={0} y1={pos.rowY + ROW_HEIGHT + ROW_GAP / 2} x2={LEFT_GUTTER}
            y2={pos.rowY + ROW_HEIGHT + ROW_GAP / 2} stroke="#18181b" />

      {/* Lock icon */}
      <foreignObject x={8} y={pos.rowY + 6} width={24} height={24}>
        <button
          type="button"
          data-testid={`task-lock-${task.id}`}
          onClick={(e) => { e.stopPropagation(); if (!readonly && onToggleLock) onToggleLock(task); }}
          title={isLocked ? "Locked — won't auto-shift" : "Cascade — shifts with predecessors"}
          className={`p-1 rounded-sm transition-colors ${
            isLocked ? "text-amber-500 hover:text-amber-400" : "text-zinc-600 hover:text-zinc-300"
          }`}
        >
          {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        </button>
      </foreignObject>

      {/* Tier 1 — Complete toggle (inline checkmark) */}
      <foreignObject x={32} y={pos.rowY + 6} width={24} height={24}>
        <button
          type="button"
          data-testid={`task-complete-${task.id}`}
          onClick={(e) => { e.stopPropagation(); if (!readonly && onToggleComplete) onToggleComplete(task); }}
          title={isDone ? "Mark incomplete" : "Mark complete"}
          className={`p-1 rounded-sm transition-colors ${
            isDone
              ? "text-emerald-500 hover:text-emerald-400"
              : "text-zinc-600 hover:text-emerald-500"
          }`}
        >
          {isDone ? <Check className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
        </button>
      </foreignObject>

      {/* Task name */}
      <text
        x={60}
        y={taskNameY}
        fill={isDone ? "#71717a" : (hasConflict ? "#fda4af" : "#e4e4e7")}
        fontSize={12}
        textDecoration={isDone ? "line-through" : "none"}
        onClick={() => onTaskClick && onTaskClick(task)}
        style={{ cursor: onTaskClick ? "pointer" : "default" }}
      >
        {truncate(task.name, 22)}
      </text>

      {/* Due-date indicator dot — red = late, green = on time. Hidden when
          done or when no due date is set. Sits just inside the gutter edge
          so it lines up vertically with the badge in the bottom Task List. */}
      {(() => {
        const status = dueDateStatus(task);
        if (status !== "late" && status !== "on_time") return null;
        const color = status === "late" ? "#fb7185" : "#34d399";
        return (
          <g data-testid={`gantt-due-status-${task.id}`}>
            <circle
              cx={236}
              cy={taskNameY - 4}
              r={4}
              fill={color}
              stroke="#09090b"
              strokeWidth={1}
            />
            <title>
              {status === "late"
                ? `Late — was due ${task.due_date}`
                : `On time — due ${task.due_date}`}
            </title>
          </g>
        );
      })()}

      {/* The bar */}
      <g style={{ cursor: readonly ? "default" : "grab" }} onPointerDown={handleBarPointerDown}>
        {task.is_milestone ? (
          <polygon
            points={`${x + 8},${barY + barH / 2} ${x + 16},${barY} ${x + 24},${barY + barH / 2} ${x + 16},${barY + barH}`}
            fill={task.color || phaseColors.bar}
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
        ) : (
          <>
            <rect
              x={x}
              y={barY}
              width={barWidth}
              height={barH}
              rx={3}
              ry={3}
              fill={task.color || phaseColors.bar}
              opacity={isDone ? 0.35 : (dragging ? 0.6 : (isLinkSource ? 0.7 : 0.85))}
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
            {task.percent_complete > 0 && (
              <rect
                x={x}
                y={barY}
                width={(barWidth * task.percent_complete) / 100}
                height={barH}
                rx={3}
                ry={3}
                fill="#fafafa"
                opacity={0.18}
              />
            )}
            {/* Avatar chip — only when bar wide enough to fit it */}
            {assignee && barWidth >= 32 && (
              <g data-testid={`task-assignee-chip-${task.id}`}>
                {/* Load ring — peak hour-load ratio across this task's span */}
                {loadRing && (
                  <circle
                    cx={x + 14}
                    cy={barY + barH / 2}
                    r={11.5}
                    fill="none"
                    stroke={loadRing}
                    strokeWidth={2}
                    data-testid={`task-load-ring-${task.id}`}
                  />
                )}
                <circle
                  cx={x + 14}
                  cy={barY + barH / 2}
                  r={9}
                  fill={colorFor(assignee.user_id)}
                  stroke="#09090b"
                  strokeWidth={1.5}
                />
                <text
                  x={x + 14}
                  y={barY + barH / 2 + 3}
                  textAnchor="middle"
                  fontSize={8}
                  fontWeight={700}
                  fill="#09090b"
                >
                  {initials(assignee.name || assignee.email)}
                </text>
                <title>{assignee.name || assignee.email}</title>
              </g>
            )}
            <text
              x={assignee && barWidth >= 32 ? x + 28 : x + 8}
              y={barY + barH / 2 + 4}
              fill="#09090b"
              fontSize={11}
              fontWeight={600}
              textDecoration={isDone ? "line-through" : "none"}
              opacity={isDone ? 0.6 : 1}
              clipPath={`inset(0 ${Math.max(0, barWidth - 100)}px 0 0)`}
            >
              {truncate(task.name, Math.max(4, Math.floor((barWidth - (assignee ? 20 : 0)) / 8)))}
            </text>
          </>
        )}
      </g>

      {/* Right-edge link handle — appears on hover */}
      {!readonly && !task.is_milestone && (hovered || isLinkSource) && onStartLink && (
        <circle
          cx={x + barWidth}
          cy={barY + barH / 2}
          r={HANDLE_RADIUS}
          fill="#f59e0b"
          stroke="#0a0a0a"
          strokeWidth={1.5}
          style={{ cursor: "crosshair" }}
          onPointerDown={(e) => onStartLink(task.id, e)}
          data-testid={`task-link-handle-${task.id}`}
        >
          <title>Drag to another task to create a Finish-to-Start dependency</title>
        </circle>
      )}
    </g>
  );
}

function truncate(str, n) {
  if (!str) return "";
  if (str.length <= n) return str;
  return str.slice(0, Math.max(0, n - 1)) + "…";
}
